import { useEffect, useState } from 'react';

import { fetchAuthenticatedBlob } from '../../api/client';
import { useAuth } from '../../features/auth/AuthProvider';
import styles from './AuthenticatedPdf.module.scss';

type AuthenticatedPdfProps = {
  readonly path: string;
  readonly title?: string;
  readonly errorLabel?: string;
};

/**
 * Renders an admin-only PDF referenced by a server-relative content path
 * (e.g. `/admin/help-articles/{id}/hero-pdf/content`). The endpoint requires a
 * Bearer token, so the bytes are fetched via the authenticated blob helper and
 * shown inline in an <iframe> from an object URL (revoked on cleanup).
 */
export function AuthenticatedPdf({ path, title = 'PDF', errorLabel }: AuthenticatedPdfProps) {
  const { tokens } = useAuth();
  const token = tokens?.access_token;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!token) return;
    let objUrl: string | null = null;
    let cancelled = false;

    setIsLoading(true);
    setHasError(false);

    const requestPath = path.startsWith('/') ? path : `/${path}`;
    fetchAuthenticatedBlob(requestPath, token)
      .then((blob) => {
        if (cancelled) return;
        // Ensure the object URL carries the PDF MIME so the browser renders it
        // inline instead of offering a download.
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        objUrl = URL.createObjectURL(pdfBlob);
        setBlobUrl(objUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [path, token]);

  if (isLoading) return <div className={styles.pdfSkeleton} />;
  if (hasError || !blobUrl) {
    return <div className={styles.pdfError}>{errorLabel ?? 'Не вдалося завантажити PDF'}</div>;
  }

  return <iframe className={styles.pdfFrame} src={blobUrl} title={title} />;
}
